import React from 'react';
import { motion } from 'framer-motion';
import { Building2Icon, PawPrintIcon, GraduationCapIcon, PlaneIcon, UserIcon, HomeIcon, CarIcon, BadgeCheckIcon, LandmarkIcon, HeartPulseIcon, StethoscopeIcon, CalendarCheckIcon, UsersIcon, ClipboardCheckIcon, KeyRoundIcon, GiftIcon, LuggageIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
type Bilingual = {
  en: string;
  fr: string;
};
type Ecosystem = {
  id: string;
  image: string;
  imageAlt: Bilingual;
  icon: typeof Building2Icon;
  label: Bilingual;
  descriptor: Bilingual;
  relationships: {
    icon: typeof UserIcon;
    label: Bilingual;
  }[];
  closing: Bilingual;
};
const ECOSYSTEMS: Ecosystem[] = [{
  id: 'cities',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/48b317ad-ea10-4be1-b97e-5e10dcb9d041.jpg',
  imageAlt: {
    en: 'Modern civic building surrounded by trees',
    fr: 'Édifice municipal moderne entouré d’arbres'
  },
  icon: Building2Icon,
  label: {
    en: 'Cities',
    fr: 'Villes'
  },
  descriptor: {
    en: 'Connected communities.',
    fr: 'Des communautés connectées.'
  },
  relationships: [{
    icon: UserIcon,
    label: {
      en: 'Resident',
      fr: 'Citoyen'
    }
  }, {
    icon: HomeIcon,
    label: {
      en: 'Household',
      fr: 'Foyer'
    }
  }, {
    icon: CarIcon,
    label: {
      en: 'Property / vehicle',
      fr: 'Propriété / véhicule'
    }
  }, {
    icon: BadgeCheckIcon,
    label: {
      en: 'Resident status',
      fr: 'Statut de résident'
    }
  }, {
    icon: LandmarkIcon,
    label: {
      en: 'City services & benefits',
      fr: 'Services municipaux et avantages'
    }
  }],
  closing: {
    en: 'A more connected city for a stronger community.',
    fr: 'Une ville plus connectée pour une communauté plus forte.'
  }
}, {
  id: 'animal-health',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/cbe9d93e-9d0e-4778-a85d-a88bb9711b3e.jpg',
  imageAlt: {
    en: 'A dog and a cat sitting together in a bright veterinary space',
    fr: 'Un chien et un chat assis ensemble dans une clinique vétérinaire lumineuse'
  },
  icon: PawPrintIcon,
  label: {
    en: 'Animal health',
    fr: 'Santé animale'
  },
  descriptor: {
    en: 'Happier pets. Healthier lives.',
    fr: 'Des animaux heureux. Des vies en santé.'
  },
  relationships: [{
    icon: UserIcon,
    label: {
      en: 'Pet owner',
      fr: 'Propriétaire de l’animal'
    }
  }, {
    icon: PawPrintIcon,
    label: {
      en: 'Pet',
      fr: 'Animal'
    }
  }, {
    icon: HeartPulseIcon,
    label: {
      en: 'Care relationship',
      fr: 'Relation de soins'
    }
  }, {
    icon: StethoscopeIcon,
    label: {
      en: 'Medical profile',
      fr: 'Profil médical'
    }
  }, {
    icon: CalendarCheckIcon,
    label: {
      en: 'Appointments & services',
      fr: 'Rendez-vous et services'
    }
  }],
  closing: {
    en: 'Better care for every companion.',
    fr: 'De meilleurs soins pour chaque compagnon.'
  }
}, {
  id: 'universities',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/18b010e3-3fcd-417a-8c9e-23dd55344f07.jpg',
  imageAlt: {
    en: 'Historic university campus building with a clock tower',
    fr: 'Édifice universitaire historique avec sa tour d’horloge'
  },
  icon: GraduationCapIcon,
  label: {
    en: 'Universities',
    fr: 'Universités'
  },
  descriptor: {
    en: 'A simpler campus experience.',
    fr: 'Une expérience simplifiée sur le campus.'
  },
  relationships: [{
    icon: UserIcon,
    label: {
      en: 'Student',
      fr: 'Étudiant'
    }
  }, {
    icon: UsersIcon,
    label: {
      en: 'Dependents',
      fr: 'Personnes à charge'
    }
  }, {
    icon: ClipboardCheckIcon,
    label: {
      en: 'Enrollment',
      fr: 'Inscription'
    }
  }, {
    icon: KeyRoundIcon,
    label: {
      en: 'Campus access',
      fr: 'Accès au campus'
    }
  }, {
    icon: GiftIcon,
    label: {
      en: 'Programs & benefits',
      fr: 'Programmes et avantages'
    }
  }],
  closing: {
    en: 'More opportunities for every journey.',
    fr: 'Plus de possibilités à chaque étape du parcours.'
  }
}, {
  id: 'airports',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/85f10b23-21cd-43e9-b94f-a60a1bb66887.jpg',
  imageAlt: {
    en: 'Travellers walking through a modern airport terminal at golden hour',
    fr: 'Voyageurs traversant un terminal d’aéroport moderne en fin de journée, dans une lumière dorée'
  },
  icon: PlaneIcon,
  label: {
    en: 'Airports',
    fr: 'Aéroports'
  },
  descriptor: {
    en: 'A smoother journey. End to end.',
    fr: 'Un parcours plus fluide, de bout en bout.'
  },
  relationships: [{
    icon: UserIcon,
    label: {
      en: 'Traveller',
      fr: 'Voyageur'
    }
  }, {
    icon: UsersIcon,
    label: {
      en: 'Companion',
      fr: 'Accompagnateur'
    }
  }, {
    icon: LuggageIcon,
    label: {
      en: 'Trip / luggage',
      fr: 'Voyage / bagages'
    }
  }, {
    icon: ShieldCheckIcon,
    label: {
      en: 'Access rights',
      fr: 'Droits d’accès'
    }
  }, {
    icon: SparklesIcon,
    label: {
      en: 'Loyalty & services',
      fr: 'Fidélité et services'
    }
  }],
  closing: {
    en: 'Seamless travel for a better experience.',
    fr: 'Un parcours fluide pour une meilleure expérience.'
  }
}];
function EcosystemCard({
  ecosystem,
  isFR
}: {
  ecosystem: Ecosystem;
  isFR: boolean;
}) {
  const Icon = ecosystem.icon;
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  return <article className="rounded-2xl border border-[#ECEDF2] bg-white overflow-hidden flex flex-col" data-id="element-1514" data-mp="10nlzf5z9w2se">
      <div className="relative" data-id="element-1515" data-mp="y8yrtqs5ucbf">
        <img src={ecosystem.image} alt={pick(ecosystem.imageAlt)} className="w-full h-44 object-cover" loading="lazy" data-id="element-1516" data-mp="u9geph6kz5j5" />
      </div>

      <div className="px-6 pb-6 flex-1 flex flex-col" data-id="element-1517" data-mp="kbs9fxbcmfmq">
        <div className="flex items-start gap-4 mt-6" data-id="element-1518" data-mp="kdd3zmxnxykl">
          <span className="w-12 h-12 shrink-0 rounded-xl bg-[#F5F5FB] flex items-center justify-center" data-id="element-1519" data-mp="1hzsnsvdkb8bb">
            <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-1520" data-mp="1czxuyma888nm" />
          </span>
          <div className="min-w-0" data-id="element-1521" data-mp="1874s63ybjae6">
            <h3 className="font-sans text-sm font-semibold tracking-[0.1em] uppercase text-[#121212]" data-id="element-1522" data-mp="13b664n0gq4gx">
              {pick(ecosystem.label)}
            </h3>
            <p className="font-sans text-sm text-[#5B6478] leading-snug mt-1.5" data-id="element-1523" data-mp="20rlg0nkt4phz">
              {pick(ecosystem.descriptor)}
            </p>
          </div>
        </div>

        <ul className="mt-6 divide-y divide-[#F0F1F5]" data-id="element-1524" data-mp="23brj6t1lkbxh">
          {ecosystem.relationships.map(relationship => {
          const RelIcon = relationship.icon;
          return <li key={relationship.label.en} className="flex items-center gap-3.5 py-3" data-id="element-1525" data-mp="2d7l34xhnb7pa">
                <span className="w-8 h-8 shrink-0 rounded-lg bg-[#F5F5FB] flex items-center justify-center" data-id="element-1526" data-mp="2i54glv6irgg6">
                  <RelIcon className="w-[15px] h-[15px] text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1527" data-mp="2r85mpvvdx7ju" />
                </span>
                <span className="font-sans text-[15px] text-[#121212] leading-snug" data-id="element-1528" data-mp="30xp4szb9wxdn">
                  {pick(relationship.label)}
                </span>
              </li>;
        })}
        </ul>

        <p className="mt-auto pt-5" data-id="element-1529" data-mp="39y58o9vxi6j1">
          <span className="block rounded-xl bg-[#F7F8FA] px-4 py-4 font-sans text-sm text-[#5B6478] text-center leading-relaxed" data-id="element-1530" data-mp="39z6hs8tdwiy4">
            {pick(ecosystem.closing)}
          </span>
        </p>
      </div>
    </article>;
}
export function EcosystemFoundation() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  return <div className="max-w-[1320px] mx-auto" data-id="element-1531" data-mp="43anv9vwaggx">
      <motion.div {...reveal} className="text-center max-w-3xl mx-auto" data-id="element-1532" data-mp="44lt7e4znwa5">
        <p className="font-sans text-xs font-semibold tracking-[0.24em] text-[#121212] uppercase" data-id="element-1533" data-mp="3cikn8a8eh7l0">
          {isFR ? 'Une fondation. Plusieurs écosystèmes.' : 'One foundation. Multiple ecosystems.'}
        </p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#121212] leading-[1.12] tracking-tight mt-5" data-id="element-1534" data-mp="3mahzzlf247lu">
          {isFR ? 'Des écosystèmes différents. ' : 'Different ecosystems. '}
          <span className="text-[#4F46E5]" data-id="element-1535" data-mp="2trkucxxsbdmg">
            {isFR ? 'Une même fondation.' : 'Same foundation.'}
          </span>
        </h2>
        <p className="font-sans text-lg text-[#5B6478] mt-5" data-id="element-1536" data-mp="2orf7dv2gmfap">
          {isFR ? 'Les relations changent. L’architecture reste la même.' : 'The relationships change. The architecture doesn’t.'}
        </p>
      </motion.div>

      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.08,
      ease: EASE
    }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-14 items-stretch" data-id="element-1537" data-mp="vblqbxi82gfp">
        {ECOSYSTEMS.map(ecosystem => <EcosystemCard key={ecosystem.id} ecosystem={ecosystem} isFR={isFR} data-id="element-1538" data-mp="1mzvswqq0brh2" />)}
      </motion.div>

      <motion.div {...reveal} transition={{
      duration: 0.6,
      delay: 0.12,
      ease: EASE
    }} className="text-center mt-16" data-id="element-1539" data-mp="1e08ri4xa2lo0">
        <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-1540" data-mp="1j27wfj8o1qn2">
          {isFR ? 'Une seule architecture relationnelle. Conçue pour ' : 'One relationship architecture. Built for '}
          <span className="text-[#4F46E5]" data-id="element-1541" data-mp="36xwczxhl8krn">
            {isFR ? 'tous les écosystèmes.' : 'any ecosystem.'}
          </span>
        </p>
        <div className="w-14 h-[2px] bg-[#4F46E5] mx-auto mt-7" aria-hidden="true" data-id="element-1542" data-mp="3axy97r4k0lzj" />
      </motion.div>
    </div>;
}